<template>
  <section class="hero-banner">
    <div class="carousel-container" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
      <div class="carousel-slide" v-for="(slide, index) in slides" :key="index">
        <img :src="slide.image" :alt="slide.title" />
        <div class="slide-overlay"></div>
      </div>
    </div>

    <div class="hero-content">
      <h1 class="hero-title">{{ t('home.hero.title') }}</h1>
      <p class="hero-subtitle">{{ t('home.hero.subtitle') }}</p>
      <div class="hero-buttons">
        <button class="btn-primary">
          {{ t('home.hero.btnDetails') }}
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
        <button class="btn-secondary" @click="navigateToAbout">
          {{ t('home.hero.btnAbout') }}
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>

    <div class="carousel-indicators">
      <span
        v-for="(slide, index) in slides"
        :key="index"
        :class="{ active: currentSlide === index }"
        @click="goToSlide(index)"
      ></span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

interface Slide {
  image: string
}

const router = useRouter()
const { t } = useI18n()
const currentSlide = ref(0)

const slides = ref<Slide[]>([
  {
    image: new URL('@/assets/carousel/slide1.webp', import.meta.url).href
  }
])

const goToSlide = (index: number) => {
  currentSlide.value = index
}

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.value.length
}

const navigateToAbout = () => {
  router.push('/about')
}

let autoPlayInterval: number | null = null

const startAutoPlay = () => {
  autoPlayInterval = window.setInterval(nextSlide, 5000)
}

const stopAutoPlay = () => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
    autoPlayInterval = null
  }
}

onMounted(() => {
  startAutoPlay()
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.hero-banner {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  margin-top: 75px;
}

.carousel-container {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-slide {
  position: relative;
  min-width: 100%;
  height: 100%;
}

.carousel-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
}

.hero-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: left;
  color: white;
  z-index: 10;
  width: 90%;
  max-width: 950px;
  padding: 0 30px;
}

.hero-title {
  font-size: 56px;
  font-weight: 700;
  margin-bottom: 28px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.hero-subtitle {
  font-size: 24px;
  margin-bottom: 45px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  font-family: 'Hind', sans-serif;
  max-width: 800px;
}

.hero-buttons {
  display: flex;
  gap: 20px;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 15px 36px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Hind', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.3px;
}

.btn-primary {
  background-color: #d97f3e;
  color: white;
  box-shadow: 0 4px 16px rgba(217, 127, 62, 0.4);
}

.btn-primary:hover {
  background-color: #c06d2f;
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(217, 127, 62, 0.5);
}

.btn-primary svg {
  transition: transform 0.3s;
}

.btn-primary:hover svg {
  transform: translateX(4px);
}

.btn-secondary {
  background-color: transparent;
  color: white;
  border: 2px solid #d97f3e;
}

.btn-secondary:hover {
  background-color: #d97f3e;
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(217, 127, 62, 0.4);
}

.btn-secondary svg {
  transition: transform 0.3s;
}

.btn-secondary:hover svg {
  transform: translateX(4px);
}

.carousel-indicators {
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 20;
}

.carousel-indicators span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-indicators span:hover {
  background-color: rgba(255, 255, 255, 0.8);
  transform: scale(1.2);
}

.carousel-indicators span.active {
  background-color: white;
  width: 32px;
  border-radius: 5px;
}

@media (max-width: 768px) {
  .hero-banner {
    margin-top: 65px;
    height: 80vh;
  }

  .hero-title {
    font-size: 38px;
  }

  .hero-subtitle {
    font-size: 17px;
    margin-bottom: 35px;
  }

  .hero-buttons {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }

  .carousel-indicators {
    bottom: 30px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 30px;
  }

  .hero-subtitle {
    font-size: 15px;
  }
}
</style>
